import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { partial } from 'underscore';
import { animateScroll } from 'react-scroll';
import Checkbox from 'components/Checkbox';
import i18n from 'utils/translate';
import NavigationButtons from 'components/NavigationButtons';
import PaymentInformationForm from 'components/PaymentInformation';
import Section from 'components/Section';
import { formatPrice } from 'utils/common';
import { toast } from 'react-toastify';
import './styles.scss';
import ErrorMessage from 'components/ErrorMessage';
import { linkTypeShape } from 'utils/types';

const propTypes = {
  type: PropTypes.oneOf([0, 1, 2]),
  quote: PropTypes.shape({
    price: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired
  }),
  onChange: PropTypes.func.isRequired,
  backLink: PropTypes.shape(linkTypeShape).isRequired,
  nextLink: PropTypes.shape(linkTypeShape).isRequired,
  calculateQuote: PropTypes.func.isRequired,
  renderSummaryBox: PropTypes.any.isRequired
};

const defaultProps = { data: {} };

class Payment extends Component {
  constructor(props) {
    super(props);

    this.state = {
      quote: props.quote,
      tos: false,
      promotion: false,
      validForm: false,
      submitted: false,
      downloadedContract: false,
      ...props.data
    };
  }

  componentDidMount() {
    const { calculateQuote, quote } = this.props;

    if (!quote) {
      const quote = calculateQuote();
      this.setState({ quote });
    }

    animateScroll.scrollToTop({ duration: 0 });
  }

  handleReverse = e => {
    const { backLink: go } = this.props;
    e.preventDefault();
    e.stopPropagation();
    go();
  };

  handleSubmit = e => {
    const {
      nextLink: { go } = {
        go: () => {}
      },
      onChange
    } = this.props;
    const form = this.refs.form;

    if (form.checkValidity && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    onChange(this.state);
    go();

    window.dataLayer.push({
      event: 'onlineabschluss',
      formname: 'Maturaball-Versicherung',
      action: 'Schritt 3 von 4'
    });
  };

  handlePaymentInfoChange = data => {
    const { submitted } = this.state;
    this.setState({ ...data }, () => {
      if (submitted) {
        this.setState({
          validForm: this.refs.form.checkValidity()
        });
      }
    });
  };

  handleDataChange = (data, key) => {
    const { submitted } = this.state;
    const { onChange } = this.props;

    this.setState(
      {
        [`${key}`]: data
      },
      () => {
        onChange(this.state);
        if (submitted) {
          this.setState({
            validForm: this.refs.form.checkValidity()
          });
        }
      }
    );
  };

  handleCheck = (key, { target: { checked: value } }) => {
    this.setState({ [key]: value });
  };

  render() {
    const { backLink, quote, renderSummaryBox } = this.props;
    const { submitted, validForm, downloadedContract, ...data } = this.state;

    const contractLink = window.documentLinks ? window.documentLinks.contractConditions : '';
    const contractDownloadClass = submitted && !downloadedContract ? 'download-error' : '';

    let finalPrice = null;
    let currency = null;

    // quote
    if (quote) {
      finalPrice = quote.price;
      currency = quote.currency;
    }

    return (
      <div className="page-step-wrapper payment row">
        <form
          ref="form"
          onSubmit={this.handleSubmit}
          className={`page-step-wrapper col-md-8 ${submitted ? 'submitted' : ''}`}
          id="insurance-form"
        >
          <PaymentInformationForm
            paymentInfo={{
              type: data.type ? data.type : 'sepa',
              iban: data.iban,
              bic: data.bic,
              showCreditCard: false,
              showPaymentSlip: true
            }}
            onPaymentInfoChange={data => {
              this.handlePaymentInfoChange(data);
            }}
          />

          <Section header={i18n('Vertragsabschluss')}>
            <div className="col-xs-12">
              <Checkbox
                id="promotionCheckbox"
                name="promotion"
                checked={data.promotion}
                onChange={partial(this.handleCheck, 'promotion')}
                label={i18n('Kontaktaufnahme zu Werbezwecken')}
                questionMark={i18n(
                  'Ich stimme zu, dass mir der Versicherer per E-Mail den GRAWE Newsletter zusenden und telefonisch, per Fax oder per E-Mail Vorschläge für Vertragsanpassungen oder andere Produkte unterbreiten kann. Diese Zustimmung kann jederzeit widerrufen werden.'
                )}
              />
            </div>
            <a
              href={contractLink}
              target="_blank"
              rel="noopener noreferrer"
              className="col-xs-12 contract-pdf"
              onClick={() => this.handleDataChange(true, 'downloadedContract')}
            >
              <span className="pdf-icon col-xs-2" />
              <span className={`col-xs-10 ${contractDownloadClass}`}>
                {i18n(
                  'Download der Versicherungsbedingungen inkl. vorvertraglicher Informationen, Informationen zum Rücktrittsrecht und zum Datenschutz'
                )}
              </span>
            </a>
            <div className="col-xs-12">
              <Checkbox
                id="tosCheckbox"
                name="tos"
                required
                checked={data.tos}
                onChange={partial(this.handleCheck, 'tos')}
                className={`${submitted && !data.tos ? 'error' : ''}`}
                label={i18n('Ich habe die oben angeführten Dokumente gelesen und akzeptiert.')}
              />
            </div>
          </Section>
          <div className="sticky-footer">
            <div className="page-step-wrapper__price d-md-none">
              {i18n('Prämie (einmalig)')}&nbsp;
              {formatPrice(finalPrice, currency)}
            </div>
            <NavigationButtons
              backLink={backLink}
              nextLink={{
                go: () => {
                  this.setState({
                    submitted: true,
                    validForm: this.refs.form.checkValidity && this.refs.form.checkValidity()
                  });

                  if (
                    downloadedContract &&
                    data.tos &&
                    this.refs.form.checkValidity &&
                    this.refs.form.checkValidity()
                  ) {
                    toast.dismiss();
                    this.handleSubmit();
                  } else {
                    toast.error(<ErrorMessage message={i18n('Bitte überprüfen Sie Ihre Angaben')} />);
                  }
                }
              }}
            />
          </div>
        </form>
        <div className="summary-wrapper col-md-4">{renderSummaryBox}</div>
      </div>
    );
  }
}

Payment.defaultProps = defaultProps;
Payment.propTypes = propTypes;

export default Payment;
